import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import type {
  BlueprintSection,
  DefinitionColumnConfig,
  DefinitionDataSection,
  LocalizedLabel,
} from '../../../types';
import { ColumnCard } from './ColumnCard';

interface SelectedColumnsListProps {
  blueprintSection: BlueprintSection;
  definitionSection: DefinitionDataSection | undefined;
  columns: NonNullable<DefinitionDataSection['columns']>;
  onRemoveColumn: (index: number) => void;
  onReorderColumn: (fromIndex: number, toIndex: number) => void;
  onUpdateColumnLabel: (index: number, label: LocalizedLabel | undefined) => void;
  onUpdateColumnType?: (index: number, config: DefinitionColumnConfig) => void;
}

interface SortableColumnItemProps {
  column: NonNullable<DefinitionDataSection['columns']>[0];
  blueprintColumn: BlueprintSection['columns'][number] | undefined;
  index: number;
  totalColumns: number;
  onRemove: () => void;
  onUpdateLabel: (label: LocalizedLabel | undefined) => void;
  onUpdateType?: (config: DefinitionColumnConfig) => void;
  onMoveToTop?: () => void;
  onMoveToBottom?: () => void;
}

function SortableColumnItem({
  column,
  blueprintColumn,
  index,
  totalColumns,
  onRemove,
  onUpdateLabel,
  onUpdateType,
  onMoveToTop,
  onMoveToBottom,
}: SortableColumnItemProps) {
  // Use stable ID that doesn't include index
  const itemId = column.id;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: itemId,
    data: {
      type: 'column',
      column,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        marginBottom: 2,
      }}
    >
      <ColumnCard
        column={column}
        blueprintColumn={blueprintColumn}
        index={index}
        totalColumns={totalColumns}
        onRemove={onRemove}
        onLabelChange={onUpdateLabel}
        onTypeChange={onUpdateType}
        onMoveToTop={onMoveToTop}
        onMoveToBottom={onMoveToBottom}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </Box>
  );
}

export function SelectedColumnsList({
  blueprintSection,
  columns,
  onRemoveColumn,
  onReorderColumn,
  onUpdateColumnLabel,
  onUpdateColumnType,
}: SelectedColumnsListProps) {
  if (columns.length === 0) {
    return (
      <Box
        sx={{
          minHeight: 200,
          padding: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          <FormattedMessage
            id="audit.reportDefinitions.selectedColumns.empty"
            defaultMessage="No hay columnas seleccionadas. Usa el botón de flecha en las columnas disponibles para agregarlas."
          />
        </Typography>
      </Box>
    );
  }

  // Use stable IDs that don't change when items are reordered
  const sortableIds = columns.map((col) => col.id);

  return (
    <Box>
      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        <Box>
          {columns.map((column, index) => {
            const blueprintColumn = blueprintSection.columns.find((c) => c.id === column.id);
            return (
              <SortableColumnItem
                key={`${column.id}:${index}`}
                column={column}
                blueprintColumn={blueprintColumn}
                index={index}
                totalColumns={columns.length}
                onRemove={() => onRemoveColumn(index)}
                onUpdateLabel={(label) => onUpdateColumnLabel(index, label)}
                onUpdateType={
                  onUpdateColumnType ? (type) => onUpdateColumnType(index, type) : undefined
                }
                onMoveToTop={index > 0 ? () => onReorderColumn(index, 0) : undefined}
                onMoveToBottom={
                  index < columns.length - 1
                    ? () => onReorderColumn(index, columns.length - 1)
                    : undefined
                }
              />
            );
          })}
        </Box>
      </SortableContext>
    </Box>
  );
}
